/**
 * <license header>
 */

import fetch, { RequestInit, Response } from 'node-fetch';
import { Headers } from './types';

class RestClient {
  /**
   * A completely raw method to make HTTP requests
   *
   * @param endpoint
   * @param method
   * @param headers
   * @param payload
   * @returns {Promise<Response>}
   */
  async makeRequest(
    endpoint: string,
    method: string = 'GET',
    headers: Headers = {},
    payload: any = null
  ): Promise<Response> {
    let options: RequestInit = {
      method: method,
      headers: headers,
    };

    if (payload !== null) {
      let body: any;
      let contentType: string | undefined;

      // Handle different payload types
      if (payload instanceof URLSearchParams) {
        // Form-encoded data
        body = payload.toString();
        contentType = headers['Content-Type'] || 'application/x-www-form-urlencoded';
      } else if (typeof FormData !== 'undefined' && payload instanceof FormData) {
        // Multipart form data - let browser set Content-Type with boundary
        body = payload;
        contentType = headers['Content-Type']; // Don't set default, browser handles this
      } else if (typeof payload === 'string') {
        // String payloads (text, XML, etc.)
        body = payload;
        contentType = headers['Content-Type'] || 'text/plain';
      } else if (
        payload instanceof Buffer ||
        payload instanceof ArrayBuffer ||
        (typeof Uint8Array !== 'undefined' && payload instanceof Uint8Array)
      ) {
        // Binary data
        body = payload;
        contentType = headers['Content-Type'] || 'application/octet-stream';
      } else {
        // Regular objects/primitives as JSON
        body = JSON.stringify(payload);
        contentType = headers['Content-Type'] || 'application/json';
      }

      // Build options with appropriate headers
      const requestHeaders = { ...headers };
      if (contentType) {
        requestHeaders['Content-Type'] = contentType;
      }

      options = {
        ...options,
        body,
        headers: requestHeaders,
      };
    }

    return await fetch(endpoint, options);
  }

  /**
   * A method to parse HTTP response
   *
   * @param response
   * @returns {Promise<any>}
   */
  async parseResponse(response: Response): Promise<any> {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle responses with no content (like 204 No Content)
    if (response.status === 204 || response.headers?.get('content-length') === '0') {
      return null;
    }

    // Try JSON first (for both real responses and mocked responses)
    if (typeof response.json === 'function') {
      const contentType = response.headers?.get('content-type');
      // If no content-type header (mocked response) or JSON content-type, parse as JSON
      if (
        !contentType ||
        contentType.includes('application/json') ||
        contentType.includes('application/hal+json')
      ) {
        return await response.json();
      }
    }

    // For non-JSON responses, return text
    if (typeof response.text === 'function') {
      const text = await response.text();
      return text;
    }

    // Fallback for responses without text/json methods
    return null;
  }

  /**
   * A generic method to make GET rest call
   *
   * @param endpoint
   * @param headers
   * @param parsed
   * @returns {Promise<Response | any>}
   */
  async get(
    endpoint: string,
    headers: Headers = {},
    parsed: boolean = true
  ): Promise<Response | any> {
    const response = await this.makeRequest(endpoint, 'GET', headers);
    return parsed ? await this.parseResponse(response) : response;
  }

  /**
   * A generic method to make POST rest call
   *
   * @param endpoint
   * @param headers
   * @param payload
   * @param parsed
   * @returns {Promise<Response | any>}
   */
  async post(
    endpoint: string,
    headers: Headers = {},
    payload: any = null,
    parsed: boolean = true
  ): Promise<Response | any> {
    const response = await this.makeRequest(endpoint, 'POST', headers, payload);
    return parsed ? await this.parseResponse(response) : response;
  }

  /**
   * A generic method to make PUT rest call
   *
   * @param endpoint
   * @param headers
   * @param payload
   * @param parsed
   * @returns {Promise<Response | any>}
   */
  async put(
    endpoint: string,
    headers: Headers = {},
    payload: any = null,
    parsed: boolean = true
  ): Promise<Response | any> {
    const response = await this.makeRequest(endpoint, 'PUT', headers, payload);
    return parsed ? await this.parseResponse(response) : response;
  }

  /**
   * A generic method to make DELETE rest call
   *
   * @param endpoint
   * @param headers
   * @param parsed
   * @returns {Promise<Response | any>}
   */
  async delete(
    endpoint: string,
    headers: Headers = {},
    parsed: boolean = true
  ): Promise<Response | any> {
    const response = await this.makeRequest(endpoint, 'DELETE', headers);
    return parsed ? await this.parseResponse(response) : response;
  }

  /**
   * A generic method to make rest call
   *
   * @param endpoint
   * @param method
   * @param headers
   * @param payload
   * @returns {Promise<any>}
   * @deprecated Use makeRequest() and parseResponse() methods instead
   */
  async apiCall(
    endpoint: string,
    method: string = 'POST',
    headers: Headers = {},
    payload: any = null
  ): Promise<any> {
    let options: RequestInit = {
      method: method,
      headers: headers,
    };

    if (payload !== null) {
      options = {
        ...options,
        body: JSON.stringify(payload),
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      };
    }

    const response: Response = await fetch(endpoint, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle responses with no content (like 204 No Content)
    if (response.status === 204 || response.headers?.get('content-length') === '0') {
      return null;
    }

    // Try JSON first (for both real responses and mocked responses)
    if (typeof response.json === 'function') {
      const contentType = response.headers?.get('content-type');
      // If no content-type header (mocked response) or JSON content-type, parse as JSON
      if (
        !contentType ||
        contentType.includes('application/json') ||
        contentType.includes('application/hal+json')
      ) {
        return await response.json();
      }
    }

    // For non-JSON responses, return text
    if (typeof response.text === 'function') {
      const text = await response.text();
      return text;
    }

    // Fallback for responses without text/json methods
    return null;
  }
}

export default RestClient;
